import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Search, Loader2, MessageSquare, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import PresenceAvatar from "@/components/PresenceAvatar";
import FollowButton from "@/components/FollowButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PeopleSearch({ onSelect, showActions = true, autoFocus = false, placeholder = "Buscar pessoas por nome ou usuário..." }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    const term = q.trim();
    if (timer.current) clearTimeout(timer.current);
    if (!term) { setResults([]); setLoading(false); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const r = await axios.get(`${API}/people/search`, { params: { q: term, limit: 20 } });
        setResults(r.data || []);
      } catch { setResults([]); }
      setLoading(false);
    }, 300);
    return () => timer.current && clearTimeout(timer.current);
  }, [q]);

  return (
    <div className="space-y-2" data-testid="people-search">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          autoFocus={autoFocus}
          data-testid="people-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={placeholder}
          className="pl-9 pr-9 rounded-full"
        />
        {q && (
          <button data-testid="people-search-clear" onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {loading && <div className="flex items-center gap-2 text-xs text-muted-foreground px-1"><Loader2 className="w-3 h-3 animate-spin" /> Buscando...</div>}
      {!loading && q.trim() && results.length === 0 && (
        <div className="text-sm text-muted-foreground px-1 py-2" data-testid="people-search-empty">Nenhuma pessoa encontrada para "{q.trim()}"</div>
      )}
      {results.length > 0 && (
        <div className="divide-y divide-border/60 max-h-80 overflow-y-auto rounded-xl border bg-background" data-testid="people-search-results">
          {results.map(u => (
            <div key={u.user_id} data-testid={`people-result-${u.user_id}`} className="flex items-center gap-3 p-2.5">
              <button onClick={() => onSelect?.(u)} className="flex items-center gap-3 flex-1 min-w-0 text-left" data-testid={`people-result-select-${u.user_id}`}>
                <PresenceAvatar src={u.picture} name={u.name} online={u.online} className="w-10 h-10" testid={`people-avatar-${u.user_id}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{u.name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {u.username ? `@${u.username}` : ""}{u.online ? " · Online" : ""}
                    {u.followers_count > 0 ? ` · ${u.followers_count} seguidores` : ""}
                  </div>
                </div>
              </button>
              {showActions && (
                <div className="flex items-center gap-1 shrink-0">
                  <FollowButton userId={u.user_id} size="sm" />
                  <Button asChild size="sm" variant="ghost" className="rounded-full px-2 text-xs" data-testid={`people-result-profile-${u.user_id}`}>
                    <Link to={`/user/${u.user_id}`}>Ver perfil</Link>
                  </Button>
                  <Button asChild size="icon" variant="ghost" className="rounded-full w-8 h-8" data-testid={`people-result-message-${u.user_id}`} title="Mensagem">
                    <Link to={`/chat/${u.user_id}`}><MessageSquare className="w-4 h-4" /></Link>
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
